'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import PricingTable from '@/components/PricingTable';
import { Calendar, TrendingUp, Bell, AlertCircle, Home, GitBranch, Link2, Activity, Shield, Eye, Lock, Check, ChevronDown } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Review { id: string }
interface BlogPost { id: string }

// ─── Testimonials ─────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  {
    name: 'Sarah Chen',
    initials: 'SC',
    company: 'Bloom Commerce',
    quote: 'Incade saved us from scaling a Meta campaign that looked great on paper but was actually cannibalizing our organic traffic. That one decision alone saved us $15K in wasted spend.',
    badge: '$15K saved',
    tag: 'Meta Ads',
  },
  {
    name: 'James Wilson',
    initials: 'JW',
    company: 'DTC Brands Co',
    quote: 'I was skeptical about another marketing tool, but Incade paid for itself in the first week. The cut recommendations alone saved us $8K in wasted spend.',
    badge: '$8K first week',
    tag: 'Meta + Google',
  },
  {
    name: 'Marcus Johnson',
    initials: 'MJ',
    company: 'TechFlow SaaS',
    quote: 'We were flying blind with platform-reported ROAS. Incade showed us our true performance was 40% lower than we thought. Now we actually trust our numbers and make decisions with confidence.',
    badge: '40% clarity',
    tag: 'Google Ads',
  },
  {
    name: 'Emily Rodriguez',
    initials: 'ER',
    company: 'StyleHouse',
    quote: "The weekly decision reports are like having a senior media buyer on staff. We\'ve reduced our cost per acquisition by 28% since switching to Incade\'s recommendations.",
    badge: '28% lower CPA',
    tag: 'TikTok Ads',
  },
  {
    name: 'David Park',
    initials: 'DP',
    company: 'GreenLeaf Foods',
    quote: 'Before Incade, we spent 10+ hours a week analyzing ad data. Now we get clear recommendations and can focus on creative strategy instead of spreadsheets.',
    badge: '10+ hrs/week',
    tag: 'Multi-platform',
  },
  {
    name: 'Lisa Thompson',
    initials: 'LT',
    company: 'FinanceFirst',
    quote: 'The confidence scores are game-changing. We know exactly when to act decisively and when to run more tests. Our ROI has improved by 35%.',
    badge: '35% ROI lift',
    tag: 'LinkedIn Ads',
  },
];

// ─── Platform SVGs ────────────────────────────────────────────────────────────
const PLATFORMS = [
  {
    name: 'Meta',
    logo: (
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28 }} fill="#0081FB">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    name: 'Google',
    logo: (
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28 }} fill="#E37400">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.25 17.292l-4.5-4.364 1.857-1.858 2.643 2.506 5.643-5.784 1.857 1.857-7.5 7.643z" />
      </svg>
    ),
  },
  {
    name: 'TikTok',
    logo: (
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28 }}>
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.07z" fill="white" />
      </svg>
    ),
  },
  {
    name: 'LinkedIn',
    logo: (
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28 }} fill="#0A66C2">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136.925-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    name: 'Snapchat',
    logo: (
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28 }} fill="#FFFC00">
        <path d="M12.166 2C9.315 2 6.698 3.498 5.22 5.916c-.78 1.275-1.153 2.748-1.153 4.168 0 .576.019 1.116.116 1.694.098.578.271 1.135.504 1.657l.562 2.566c.097-.058.194-.136.31-.155l-.872 2.721c.581.174 1.212.271 1.89.271 2.43 0 3.603-1.522 3.603-3.023 0-1.657-1.29-2.43-2.43-3.1-1.077-.639-1.444-1.038-1.444-1.6 0-.794.639-1.154 1.522-1.154 1.077 0 2.14.349 2.14.349s1.251 1.212 1.386 1.348v-.019L15.337 23.98z" />
      </svg>
    ),
  },
  {
    name: 'Shopify',
    logo: (
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28 }} fill="#96BF48">
        <path d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.116-.194-.213-.194-.098 0-1.89-.039-1.89-.039s-1.251-1.212-1.386-1.348v.019L15.337 23.98z" />
      </svg>
    ),
  },
  {
    name: 'Stripe',
    logo: (
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28 }} fill="#635BFF">
        <path d="M12.165 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12.165 0zm-1.25 17.292l-4.5-4.364 1.857-1.858 2.643 2.506 5.643-5.784 1.857 1.857-7.5 7.643z" />
      </svg>
    ),
  },
  {
    name: 'GA4',
    logo: (
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28 }} fill="#E37400">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.25 17.292l-4.5-4.364 1.857-1.858 2.643 2.506 5.643-5.784 1.857 1.857-7.5 7.643z" />
      </svg>
    ),
  },
];

// ─── FAQ Data ─────────────────────────────────────────────────────────────────
const FAQ_ITEMS = [
  {
    question: 'Is my ad account data secure?',
    answer: 'Absolutely. Incade uses read-only OAuth connections, AES-256 encryption, and GDPR/CCPA-compliant architecture. We never modify your campaigns or budgets without explicit permission. You can revoke access anytime from your dashboard.',
  },
  {
    question: 'How long does setup take?',
    answer: 'Most teams are up and running in under 5 minutes. Just connect your ad platforms via one-click OAuth (Meta, Google, TikTok, etc.) and optionally link a revenue source like Shopify or Stripe. Your first recommendations arrive within minutes.',
  },
  {
    question: 'What happens after the 7-day free trial?',
    answer: "Your trial gives you full access to all features. After 7 days, you can choose a plan that fits your needs. No credit card is required upfront, and there are no surprise charges. If you don't subscribe, your account simply becomes read-only.",
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes, absolutely. There are no long-term contracts or cancellation fees. You can cancel from your Settings page at any time, and your subscription will remain active until the end of the current billing period.',
  },
  {
    question: 'What if an ad platform API goes down?',
    answer: 'Incade is built with platform resilience in mind. If an API experiences downtime, we use fallback data sources and clearly indicate any data gaps. Your decision engine keeps running even when individual platforms don\'t.',
  },
  {
    question: 'Do you support agencies with multiple clients?',
    answer: 'Yes. Our Growth and Scale plans include agency features like a white-label client portal, multi-client management, team collaboration tools, and client-specific reporting. Each client gets their own isolated workspace.',
  },
  {
    question: 'How is Incade different from native ad platform dashboards?',
    answer: 'Native dashboards show you platform-specific metrics that often conflict with each other. Incade blends data across all your platforms and cross-references it with your actual revenue to give you a unified, trustworthy view — plus actionable Cut/Keep/Scale decisions you won\'t find anywhere else.',
  },
];

// ─── Card hover helpers ───────────────────────────────────────────────────────
const cardHoverOn = (e: React.MouseEvent<HTMLDivElement>) => {
  const el = e.currentTarget as HTMLElement;
  el.style.borderColor = 'rgba(249,115,22,0.2)';
  el.style.transform = 'translateY(-3px)';
};
const cardHoverOff = (e: React.MouseEvent<HTMLDivElement>) => {
  const el = e.currentTarget as HTMLElement;
  el.style.borderColor = 'rgba(255,255,255,0.06)';
  el.style.transform = 'translateY(0)';
};

// ─── Section label ────────────────────────────────────────────────────────────
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '0.12em', textTransform: 'uppercase' as const, display: 'block', marginBottom: '18px' }}>
      {children}
    </span>
  );
}

// ─── Section heading ──────────────────────────────────────────────────────────
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 900, letterSpacing: '-0.03em', color: 'white', lineHeight: 1.1, marginBottom: '16px' }}>
      {children}
    </h2>
  );
}

// ─── FAQ Item ─────────────────────────────────────────────────────────────────
function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'border-color 0.2s ease',
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          gap: '16px',
        }}
      >
        <span style={{ fontSize: '15px', fontWeight: 600, color: 'white', lineHeight: 1.4 }}>{question}</span>
        <ChevronDown
          size={18}
          style={{
            color: '#F97316',
            flexShrink: 0,
            transition: 'transform 0.2s ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>
      {open && (
        <div style={{ padding: '0 24px 20px' }}>
          <p style={{ fontSize: '14px', color: '#94A3B8', lineHeight: 1.7, margin: 0 }}>{answer}</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HomePage() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [reviews, setReviews] = useState<Review[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const howItWorksRef = useRef<HTMLElement>(null);

  return (
    <>
      <PublicNav />
      <main style={{ background: '#060B14' }}>
        {/* JSON-LD */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: 'Incade',
              url: 'https://incade.io',
              logo: 'https://incade.io/assets/images/app_logo.png',
              contactPoint: { '@type': 'ContactPoint', email: 'info@incade.io', contactType: 'customer service' },
            }),
          }}
        />

        {/* ── SECTION 1 — HERO ── */}
        <section
          style={{
            background: '#060B14',
            padding: '90px 40px 72px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(ellipse 65% 45% at 50% 0%, rgba(249,115,22,0.09), transparent 65%), radial-gradient(ellipse 40% 30% at 85% 60%, rgba(99,102,241,0.04), transparent)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              WebkitMaskImage: 'radial-gradient(ellipse 75% 75% at 50% 40%, black, transparent)',
              maskImage: 'radial-gradient(ellipse 75% 75% at 50% 40%, black, transparent)',
              pointerEvents: 'none',
            }}
          />

          <div style={{ position: 'relative', maxWidth: '900px', margin: '0 auto' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                background: 'rgba(249,115,22,0.07)',
                border: '1px solid rgba(249,115,22,0.18)',
                color: '#FB923C',
                padding: '5px 15px',
                borderRadius: '100px',
                fontSize: '12px',
                fontWeight: 600,
                marginBottom: '28px',
                animation: 'fadeUp 0.4s ease both',
                animationDelay: '0s',
              }}
            >
              <span
                style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '50%',
                  background: '#F97316',
                  flexShrink: 0,
                  animation: 'pulse 2s ease-in-out infinite',
                }}
              />
              Weekly decisions. Real revenue. Zero guesswork.
            </div>

            <h1
              style={{
                fontSize: 'clamp(36px, 5vw, 58px)',
                fontWeight: 900,
                letterSpacing: '-0.04em',
                lineHeight: 1.05,
                color: 'white',
                maxWidth: '820px',
                margin: '0 auto 22px',
                animation: 'fadeUp 0.4s ease both',
                animationDelay: '0.1s',
              }}
            >
              Stop guessing. Start knowing which ads to{' '}
              <span style={{ color: '#EF4444' }}>cut</span>,{' '}
              <span style={{ color: '#FBBF24' }}>keep</span>, or{' '}
              <span style={{ color: '#22C55E' }}>scale</span>.
            </h1>

            <p
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: 'white',
                maxWidth: '600px',
                margin: '0 auto 14px',
                lineHeight: 1.4,
                animation: 'fadeUp 0.4s ease both',
                animationDelay: '0.2s',
              }}
            >
              Get Clear Decisions in 10 Minutes.
            </p>

            <p
              style={{
                fontSize: '16px',
                color: '#94A3B8',
                maxWidth: '580px',
                margin: '0 auto 40px',
                lineHeight: 1.65,
                animation: 'fadeUp 0.4s ease both',
                animationDelay: '0.25s',
              }}
            >
              Incade analyzes your ad spend across Meta, Google, TikTok, LinkedIn, and Snapchat — then gives you weekly decisions backed by real revenue data.
            </p>

            <div
              style={{
                display: 'flex',
                gap: '14px',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: '20px',
                animation: 'fadeUp 0.4s ease both',
                animationDelay: '0.3s',
              }}
            >
              <Link
                href="/start"
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #F97316, #EA580C)',
                  color: 'white',
                  border: 'none',
                  padding: '15px 36px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 700,
                  textDecoration: 'none',
                  boxShadow: '0 0 40px rgba(249,115,22,0.28)',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 60px rgba(249,115,22,0.42)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 40px rgba(249,115,22,0.28)';
                }}
              >
                Start Saving Ad Spend →
              </Link>
              <Link
                href="/features"
                style={{
                  display: 'inline-block',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#CBD5E1',
                  border: '1px solid rgba(255,255,255,0.09)',
                  padding: '15px 36px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.background = 'rgba(249,115,22,0.1)';
                  el.style.color = 'white';
                  el.style.borderColor = 'rgba(249,115,22,0.3)';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.background = 'rgba(255,255,255,0.05)';
                  el.style.color = '#CBD5E1';
                  el.style.borderColor = 'rgba(255,255,255,0.09)';
                }}
              >
                See the Dashboard
              </Link>
            </div>

            <p
              style={{
                fontSize: '12px',
                color: '#64748B',
                marginBottom: '52px',
                animation: 'fadeUp 0.4s ease both',
                animationDelay: '0.35s',
              }}
            >
              No credit card required · Cancel anytime · Setup in under 5 minutes
            </p>

            {/* Hero Mockup */}
            <div
              style={{
                maxWidth: '1000px',
                margin: '0 auto',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                overflow: 'hidden',
                animation: 'fadeUp 0.4s ease both',
                animationDelay: '0.4s',
              }}
            >
              <div
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr',
                  padding: '14px 24px',
                  gap: '12px',
                }}
              >
                {['Campaign', 'Meta Spend', 'Shopify Rev', 'Action'].map((col) => (
                  <span key={col} style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{col}</span>
                ))}
              </div>
              {[
                { campaign: 'Summer Sale — Broad', spend: '$4,200', rev: '$6,100', action: 'Cut', actionColor: '#EF4444', actionBg: 'rgba(239,68,68,0.12)' },
                { campaign: 'Retargeting — 30d', spend: '$1,800', rev: '$9,400', action: 'Keep', actionColor: '#FBBF24', actionBg: 'rgba(251,191,36,0.12)' },
                { campaign: 'Lookalike — 1%', spend: '$2,100', rev: '$14,200', action: 'Scale', actionColor: '#22C55E', actionBg: 'rgba(34,197,94,0.12)' },
              ].map((row, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr',
                    padding: '16px 24px',
                    gap: '12px',
                    borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                    background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: '13px', color: '#CBD5E1', fontWeight: 500 }}>{row.campaign}</span>
                  <span style={{ fontSize: '13px', color: '#94A3B8' }}>{row.spend}</span>
                  <span style={{ fontSize: '13px', color: '#94A3B8' }}>{row.rev}</span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px 12px',
                      borderRadius: '100px',
                      fontSize: '12px',
                      fontWeight: 700,
                      color: row.actionColor,
                      background: row.actionBg,
                      width: 'fit-content',
                    }}
                  >
                    {row.action}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 3 — THE PROBLEM ── */}
        <section
          style={{
            background: 'rgba(255,255,255,0.01)',
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            padding: '88px 40px',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: '960px', margin: '0 auto' }}>
            <SectionLabel>The problem</SectionLabel>
            <SectionHeading>Ad Platforms Only Show You What They Want You to See</SectionHeading>
            <p style={{ fontSize: '16px', color: '#94A3B8', lineHeight: 1.65, maxWidth: '640px', margin: '0 auto 56px' }}>
              You&apos;re not imagining it. Your marketing data is fragmented — and it&apos;s costing you money every week. Here&apos;s what&apos;s actually happening behind the scenes.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '18px',
                maxWidth: '860px',
                margin: '0 auto',
              }}
            >
              {/* Left card — red */}
              <div
                style={{
                  background: 'rgba(239,68,68,0.04)',
                  border: '1px solid rgba(239,68,68,0.1)',
                  borderRadius: '18px',
                  padding: '32px',
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                  <AlertCircle size={16} style={{ color: '#F87171' }} />
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#F87171', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    Platform ROAS — Unreliable
                  </span>
                </div>
                {[
                  { label: 'Meta Ads ROAS', val: '4.2x' },
                  { label: 'Google Ads ROAS', val: '3.8x' },
                  { label: 'TikTok ROAS', val: '2.9x' },
                  { label: 'LinkedIn ROAS', val: '3.1x' },
                ].map((row) => (
                  <div
                    key={row.label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: 'rgba(239,68,68,0.04)',
                      marginBottom: '8px',
                    }}
                  >
                    <span style={{ fontSize: '13px', color: '#94A3B8' }}>{row.label}</span>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#F87171' }}>{row.val}</span>
                  </div>
                ))}
                <p style={{ fontSize: '12px', color: '#991B1B', fontStyle: 'italic', marginTop: '12px' }}>
                  Each platform claims credit for the same sale. Double-counting is the default.
                </p>
              </div>

              {/* Right card — orange */}
              <div
                style={{
                  background: 'rgba(249,115,22,0.04)',
                  border: '1px solid rgba(249,115,22,0.14)',
                  borderRadius: '18px',
                  padding: '32px',
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                  <Check size={16} style={{ color: '#F97316' }} />
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    Blended ROAS — The Truth
                  </span>
                </div>
                {[
                  { label: 'Total Spend', val: '$10,000' },
                  { label: 'Actual Shopify Rev', val: '$22,000' },
                  { label: 'Blended ROAS', val: '2.2x' },
                  { label: 'Meta contribution', val: '38%' },
                ].map((row) => (
                  <div
                    key={row.label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: 'rgba(249,115,22,0.04)',
                      marginBottom: '8px',
                    }}
                  >
                    <span style={{ fontSize: '13px', color: '#94A3B8' }}>{row.label}</span>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#F97316' }}>{row.val}</span>
                  </div>
                ))}
                <p style={{ fontSize: '12px', color: '#166534', fontStyle: 'italic', marginTop: '12px' }}>
                  One number. Real revenue. No double-counting.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 4 — HOW IT WORKS ── */}
        <section ref={howItWorksRef} style={{ background: '#060B14', padding: '88px 40px', textAlign: 'center' }}>
          <div style={{ maxWidth: '960px', margin: '0 auto' }}>
            <SectionLabel>How it works</SectionLabel>
            <SectionHeading>Three steps. One decision engine.</SectionHeading>
            <p style={{ fontSize: '16px', color: '#94A3B8', lineHeight: 1.65, marginBottom: '56px' }}>
              No data team. No spreadsheets. No guessing. Connect, analyze, act.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '1px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '18px',
                overflow: 'hidden',
                maxWidth: '940px',
                margin: '0 auto',
              }}
            >
              {[
                { step: 'STEP 01', Icon: Link2, title: 'Connect in minutes', desc: 'Authorize ad platforms via official OAuth. Read-only access.' },
                { step: 'STEP 02', Icon: Activity, title: 'We analyze everything', desc: 'Real revenue vs channel spend. Blended ROAS. True attribution.' },
                { step: 'STEP 03', Icon: Calendar, title: 'Decisions every Monday', desc: null },
              ].map((s) => (
                <div key={s.step} style={{ background: '#060B14', padding: '40px 32px', textAlign: 'center' }}>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'rgba(249,115,22,0.1)',
                      border: '1px solid rgba(249,115,22,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                    }}
                  >
                    <s.Icon size={22} style={{ color: '#F97316' }} />
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '0.1em', marginBottom: '10px' }}>{s.step}</div>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'white', marginBottom: '10px' }}>{s.title}</h3>
                  {s.desc ? (
                    <p style={{ fontSize: '14px', color: '#94A3B8', lineHeight: 1.6 }}>{s.desc}</p>
                  ) : (
                    <p style={{ fontSize: '14px', color: '#94A3B8', lineHeight: 1.6 }}>
                      <span style={{ color: '#EF4444', fontWeight: 600 }}>Cut</span> what drains budget.{' '}
                      <span style={{ color: '#FBBF24', fontWeight: 600 }}>Keep</span> what is stable.{' '}
                      <span style={{ color: '#22C55E', fontWeight: 600 }}>Scale</span> what works.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 5 — FEATURES ── */}
        <section style={{ background: '#060B14', padding: '88px 40px', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <div style={{ maxWidth: '960px', margin: '0 auto' }}>
            <SectionLabel>Features</SectionLabel>
            <SectionHeading>Built to make the right call.</SectionHeading>
            <p style={{ fontSize: '16px', color: '#94A3B8', lineHeight: 1.65, marginBottom: '56px' }}>
              No fluff. Just the intelligence layer that shows where your budget actually works.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '14px',
              }}
            >
              {[
                { Icon: Calendar, title: 'Weekly Decision Engine', desc: 'Cut, Keep, or Scale decisions delivered every Monday morning based on real revenue data.' },
                { Icon: TrendingUp, title: 'Blended ROAS Intelligence', desc: 'Real revenue from Shopify or Stripe divided by total ad spend. No double-counting.' },
                { Icon: Bell, title: 'Automated Alerts', desc: 'CPA spikes, revenue drops, and spend anomalies detected and sent to your inbox automatically.' },
                { Icon: AlertCircle, title: 'Creative Fatigue Detection', desc: 'Catch declining creatives before they drain your budget with trend monitoring.' },
                { Icon: Home, title: 'Agency Dashboard', desc: 'Manage multiple client accounts from one place with per-client decisions and reports.' },
                { Icon: GitBranch, title: 'What-If Scenarios', desc: 'Simulate budget changes and see estimated revenue outcomes before acting.' },
              ].map((feature) => (
                <div
                  key={feature.title}
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '16px',
                    padding: '28px',
                    textAlign: 'left',
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={cardHoverOn}
                  onMouseLeave={cardHoverOff}
                >
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '10px',
                      background: 'rgba(249,115,22,0.1)',
                      border: '1px solid rgba(249,115,22,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <feature.Icon size={20} style={{ color: '#F97316' }} />
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'white', marginBottom: '8px' }}>{feature.title}</h3>
                  <p style={{ fontSize: '14px', color: '#94A3B8', lineHeight: 1.6 }}>{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 6 — TESTIMONIALS ── */}
        <section
          style={{
            background: 'rgba(255,255,255,0.01)',
            borderTop: '1px solid rgba(255,255,255,0.04)',
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            padding: '88px 40px',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <SectionLabel>Trusted by performance marketers</SectionLabel>
            <SectionHeading>See How Marketing Teams Use Incade</SectionHeading>
            <p style={{ fontSize: '16px', color: '#94A3B8', lineHeight: 1.65, marginBottom: '56px' }}>
              Real results from brands making smarter ad spend decisions.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '20px',
              }}
            >
              {TESTIMONIALS.map((t) => (
                <div
                  key={t.name}
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '16px',
                    padding: '32px',
                    textAlign: 'left',
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={cardHoverOn}
                  onMouseLeave={cardHoverOff}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #F97316, #EA580C)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: 'white',
                        flexShrink: 0,
                      }}
                    >
                      {t.initials}
                    </div>
                    <div>
                      <h4 style={{ fontSize: '15px', fontWeight: 700, color: 'white', marginBottom: '2px' }}>{t.name}</h4>
                      <p style={{ fontSize: '13px', color: '#64748B' }}>{t.company}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: '14px', color: '#94A3B8', lineHeight: 1.6, marginBottom: '24px' }}>
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop: '1px solid rgba(255,255,255,0.05)',
                      paddingTop: '16px',
                    }}
                  >
                    <span
                      style={{
                        background: 'rgba(34,197,94,0.15)',
                        color: '#4ADE80',
                        padding: '2px 8px',
                        borderRadius: '100px',
                        fontSize: '11px',
                        fontWeight: 700,
                      }}
                    >
                      {t.badge}
                    </span>
                    <span style={{ color: '#64748B', fontSize: '12px', fontWeight: 500 }}>{t.tag}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 7 — PRICING ── */}
        <section style={{ background: '#060B14', padding: '88px 40px 0', textAlign: 'center' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <SectionLabel>Pricing</SectionLabel>
            <SectionHeading>Simple, Transparent Pricing</SectionHeading>
            <p style={{ fontSize: '16px', color: '#94A3B8', lineHeight: 1.65, marginBottom: '36px' }}>
              Start with a 7-day free trial. No credit card required.
            </p>
          </div>
          <PricingTable showHero={false} />
        </section>

        {/* ── SECTION 8 — SECURITY ── */}
        <section
          style={{
            background: 'rgba(255,255,255,0.01)',
            borderTop: '1px solid rgba(255,255,255,0.04)',
            padding: '88px 40px',
          }}
        >
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '60px',
                alignItems: 'start',
              }}
            >
              {/* Left: Copy */}
              <div>
                <SectionLabel>Security &amp; Access Control</SectionLabel>
                <h2
                  style={{
                    fontSize: 'clamp(26px, 3vw, 38px)',
                    fontWeight: 900,
                    letterSpacing: '-0.03em',
                    color: 'white',
                    lineHeight: 1.1,
                    marginBottom: '16px',
                  }}
                >
                  Your Data Stays Yours
                </h2>
                <p style={{ fontSize: '16px', color: '#94A3B8', lineHeight: 1.65, marginBottom: '28px' }}>
                  We understand that connecting your ad accounts requires trust. That&apos;s why we&apos;ve built Incade with security-first principles at every level.
                </p>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[
                    'Uses official Meta, Google, LinkedIn and TikTok APIs',
                    'OAuth 2.0 authentication',
                    'GDPR & CCPA compliant architecture',
                    'Read-only access by default',
                    'Industry-standard encryption (AES-256)',
                  ].map((item) => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <Check size={15} style={{ color: '#22C55E', flexShrink: 0, marginTop: '2px' }} />
                      <span style={{ fontSize: '14px', color: '#94A3B8', lineHeight: 1.5 }}>{item}</span>
                    </li>
                  ))}
                </ul>

                <blockquote
                  style={{
                    background: 'rgba(249,115,22,0.05)',
                    border: '1px solid rgba(249,115,22,0.15)',
                    borderLeft: '3px solid #F97316',
                    borderRadius: '8px',
                    padding: '16px 20px',
                    margin: 0,
                  }}
                >
                  <p style={{ fontSize: '13px', color: '#CBD5E1', lineHeight: 1.65, fontStyle: 'italic', margin: 0 }}>
                    &ldquo;We treat your data with the same care we&apos;d want for our own business. Security isn&apos;t a feature — it&apos;s the foundation.&rdquo;
                  </p>
                </blockquote>
              </div>

              {/* Right: Security Card */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '16px',
                  padding: '32px',
                }}
              >
                {[
                  { Icon: Eye, title: 'Read-Only by Default', desc: 'Incade only reads your data. We never modify campaigns, budgets, or settings without explicit permission.' },
                  { Icon: Lock, title: 'Industry-Standard Encryption', desc: 'All data is encrypted in transit and at rest using AES-256. Your credentials are never stored in plain text.' },
                  { Icon: Shield, title: 'Revoke Anytime', desc: 'Disconnect any platform instantly from your dashboard. We delete associated account data within 30 days, unless retention is required by law.' },
                ].map((item, i) => (
                  <div
                    key={item.title}
                    style={{
                      display: 'flex',
                      gap: '16px',
                      paddingBottom: i < 2 ? '24px' : '0',
                      marginBottom: i < 2 ? '24px' : '0',
                      borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'rgba(249,115,22,0.1)',
                        border: '1px solid rgba(249,115,22,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <item.Icon size={18} style={{ color: '#F97316' }} />
                    </div>
                    <div>
                      <h4 style={{ fontSize: '15px', fontWeight: 700, color: 'white', marginBottom: '6px' }}>{item.title}</h4>
                      <p style={{ fontSize: '13px', color: '#94A3B8', lineHeight: 1.6 }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 9 — FINAL CTA ── */}
        <section
          style={{
            background: '#060B14',
            padding: '100px 40px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(249,115,22,0.08), transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'relative', maxWidth: '700px', margin: '0 auto' }}>
            <SectionLabel>Get Started</SectionLabel>
            <h2
              style={{
                fontSize: 'clamp(28px, 4vw, 46px)',
                fontWeight: 900,
                letterSpacing: '-0.03em',
                color: 'white',
                lineHeight: 1.1,
                marginBottom: '16px',
              }}
            >
              Start optimizing in less than 10 minutes
            </h2>
            <p
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: 'white',
                marginBottom: '12px',
              }}
            >
              Stop Wasting Ad Spend. Start Making Smarter Decisions.
            </p>
            <p style={{ fontSize: '16px', color: '#94A3B8', lineHeight: 1.65, marginBottom: '40px' }}>
              Connect your platforms, get your first Cut/Keep/Scale recommendations, and start optimizing — all within your free trial.
            </p>
            <Link
              href="/start"
              style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #F97316, #EA580C)',
                color: 'white',
                padding: '18px 44px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 700,
                textDecoration: 'none',
                boxShadow: '0 0 50px rgba(249,115,22,0.2)',
                transition: 'all 0.2s ease',
                marginBottom: '20px',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 0 70px rgba(249,115,22,0.45)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 0 50px rgba(249,115,22,0.2)';
              }}
            >
              Start 7-Day Free Trial
            </Link>
            <p style={{ fontSize: '13px', color: '#64748B', marginTop: '16px' }}>
              ✔️ No credit card required · ✔️ Read-only access · ✔️ Cancel anytime
            </p>
          </div>
        </section>

        {/* ── SECTION 10 — FAQ ── */}
        <section
          style={{
            background: 'rgba(255,255,255,0.01)',
            borderTop: '1px solid rgba(255,255,255,0.04)',
            padding: '88px 40px',
          }}
        >
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <SectionLabel>FAQ</SectionLabel>
              <SectionHeading>Everything you need to know before getting started.</SectionHeading>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {FAQ_ITEMS.map((item) => (
                <FAQItem key={item.question} question={item.question} answer={item.answer} />
              ))}
            </div>

            <div style={{ textAlign: 'center', marginTop: '48px' }}>
              <Link
                href="/help"
                style={{
                  display: 'inline-block',
                  background: 'rgba(249,115,22,0.1)',
                  color: '#F97316',
                  border: '1px solid rgba(249,115,22,0.25)',
                  padding: '14px 32px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.background = 'rgba(249,115,22,0.18)';
                  el.style.borderColor = 'rgba(249,115,22,0.4)';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.background = 'rgba(249,115,22,0.1)';
                  el.style.borderColor = 'rgba(249,115,22,0.25)';
                }}
              >
                Visit Help Center →
              </Link>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.85); }
        }
        .hidden-mobile { display: grid; }
        .show-mobile { display: none; }
        @media (max-width: 767px) {
          .hidden-mobile { display: none !important; }
          .show-mobile { display: block !important; }
        }
      `}</style>
    </>
  );
}
